'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import classes from './CookieBanner.module.scss';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { GA_ID } from '@/src/helpers/gtag';

const COOKIE_NAME = 'ga_consent'; // "granted" | "denied"

function setConsentCookie(value: 'granted' | 'denied') {
  // 6 months
  const maxAge = 60 * 60 * 24 * 183;
  document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

function getCookie(name: string) {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

function updateGtagConsent(granted: boolean) {
  if (typeof window === 'undefined') {
    return;
  }
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  function gtag(...args: any[]) {
    w.dataLayer.push(args);
  }
  gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
  });
}

function injectGtagScripts() {
  if (typeof document === 'undefined' || document.getElementById('gtag-consent-script')) {
    return;
  }

  const consentScript = document.createElement('script');
  consentScript.id = 'gtag-consent-script';
  consentScript.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'granted'
    });
  `;
  document.head.appendChild(consentScript);

  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(gtagScript);

  const initScript = document.createElement('script');
  initScript.id = 'gtag-init-script';
  initScript.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${GA_ID}', {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });
  `;
  document.head.appendChild(initScript);
}

export default function CookieBanner() {
  const dictionary = useDictionary();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const current = getCookie(COOKIE_NAME);
    if (!current) {
      setShow(true);
    }
  }, []);

  const handleDeny = () => {
    setConsentCookie('denied');
    updateGtagConsent(false);
    setShow(false);
  };

  const handleAccept = () => {
    setConsentCookie('granted');
    injectGtagScripts();
    updateGtagConsent(true);
    setShow(false);
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <div className={classes.backdrop} aria-hidden="true" />
      <div
        className={classes.banner}
        role="dialog"
        aria-live="polite"
        aria-labelledby="cookie-dialog-title"
      >
        <div className={classes.card}>
          <div className={classes.visual} aria-hidden="true">
            <Image
              src="/Averiane-cookie-bird-cookie-banner.webp"
              alt=""
              width={120}
              height={170}
              className={classes.mascot}
              priority
            />
          </div>
          <div className={classes.body}>
            <div className={classes.content}>
              <h2 id="cookie-dialog-title" className={classes.title}>
                {dictionary.general.cookieTitle}
              </h2>
              <p className={classes.text}>{dictionary.general.cookie}</p>
            </div>
            <div className={classes.actions}>
              <button type="button" className={classes.acceptBtn} onClick={handleAccept}>
                {dictionary.general.accept}
              </button>
              <button type="button" className={classes.denyBtn} onClick={handleDeny}>
                {dictionary.general.deny}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
